import { Button } from "@city-sage/aurum-avis-labs-components";
import { ArrowLeft, Share2, MapPin } from "lucide-react";
import { Stop, Day, StopStateEnum } from "@city-sage/core-client";
import { FC, useRef, useMemo, useEffect } from 'react';
import Map, { Marker, MapRef, Source, Layer, GeolocateControl } from 'react-map-gl/mapbox';
import { MAPBOX_ACCESS_TOKEN } from '../../lib/map/token';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useRouter } from "@tanstack/react-router";
import { DAY_COLOR_HEX } from '../../lib/constants/dayColors';
import { useDayStops } from "../../hooks/useDayStops";

export type SectionType = "overview" | "day" | "stop"

export interface Section {
  id: string
  type: SectionType
  dayIndex?: number
  stopIndex?: number
  title: string
}

interface ItineraryMapSectionProps {
  days: Day[] | undefined
  stops: Stop[] | undefined
  selectedStop: Stop | null
  visibleStop: Stop | null | undefined
  visibleDay: Day | null | undefined
  isOverviewVisible: boolean
  isScrolling: boolean
  onStopSelect: (stop: Stop, dayNumber: number, stopNumber: number) => void
  onStopClick: (stop: Stop, dayIndex: number, stopIndex: number) => void
  currentSection: Section
}

export const ItineraryMapSection = ({ days, stops, selectedStop, visibleStop, visibleDay, isOverviewVisible, isScrolling, onStopSelect, onStopClick, currentSection }: ItineraryMapSectionProps) => {
  const mapRef = useRef<MapRef | null>(null);
  const router = useRouter();

  const { data: dayStopsData } = useDayStops({ 
    itineraryId: visibleDay?.itineraryId || '', 
    dayId: visibleDay?.id || '',
    states: [StopStateEnum.Accepted]
  });

  // Filter stops with valid coordinates
  const validStops = useMemo(() => {
    if (!stops) return [];
    return stops.filter(stop => 
      stop.place.coordinates?.latitude && 
      stop.place.coordinates?.longitude &&
      !isNaN(stop.place.coordinates.latitude) &&
      !isNaN(stop.place.coordinates.longitude)
    );
  }, [stops]);

  // Zoom to visible stop when it changes (but not during scrolling)
  useEffect(() => {
    if (visibleStop && mapRef.current && 
        visibleStop.place.coordinates?.latitude && 
        visibleStop.place.coordinates?.longitude &&
        !isScrolling) {
      
      const map = mapRef.current.getMap();
      
      // Smooth fly to the visible stop
      map.flyTo({
        center: [visibleStop.place.coordinates.longitude, visibleStop.place.coordinates.latitude],
        zoom: 15,
        duration: 2000, // Slightly faster for snap scrolling
        essential: true
      });
    }
  }, [visibleStop, isScrolling]);

  // Fit bounds to show all stops for the visible day (but not during scrolling)
  useEffect(() => {
    if (visibleDay && mapRef.current && dayStopsData?.length && !isScrolling) {
      const map = mapRef.current.getMap();
      
      // Get stops with valid coordinates for this day
      const dayStops = dayStopsData?.filter(stop => 
        stop.place.coordinates?.latitude && 
        stop.place.coordinates?.longitude &&
        !isNaN(stop.place.coordinates.latitude) &&
        !isNaN(stop.place.coordinates.longitude)
      );

      if (dayStops.length === 0) return;

      if (dayStops.length === 1) {
        // Single stop - just fly to it
        const stop = dayStops[0];
        map.flyTo({
          center: [stop.place.coordinates!.longitude, stop.place.coordinates!.latitude],
          zoom: 14,
          duration: 2000,
          essential: true
        });
      } else {
        // Multiple stops - fit bounds
        const lngs = dayStops.map(stop => stop.place.coordinates!.longitude);
        const lats = dayStops.map(stop => stop.place.coordinates!.latitude);
        
        const bounds = {
          minLng: Math.min(...lngs),
          maxLng: Math.max(...lngs),
          minLat: Math.min(...lats),
          maxLat: Math.max(...lats)
        };

        setTimeout(() => {
          map.fitBounds(
            [
              [bounds.minLng, bounds.minLat],
              [bounds.maxLng, bounds.maxLat]
            ],
            {
              padding: { top: 40, bottom: 100, left: 40, right: 40 },
              duration: 2000,
              maxZoom: 16
            }
          );
        }, 100);
      }
    }
  }, [visibleDay, isScrolling]);

  // Get active stops based on current section
  const getActiveStops = (): string[] => {
    if (!validStops || !days) return [];
    
    // If we have a visible stop, highlight it
    if (visibleStop) {
      return [visibleStop.id];
    }
    
    switch (currentSection.type) {
      case 'overview':
        return validStops.map(stop => stop.id);
      
      case 'day':
        if (currentSection.dayIndex !== undefined) {
          const currentDay = days[currentSection.dayIndex];
          if (currentDay) {
            return validStops
              .filter(stop => stop.dayIds?.includes(currentDay.id))
              .map(stop => stop.id);
          }
        }
        return [];
      
      case 'stop':
        if (currentSection.dayIndex !== undefined && currentSection.stopIndex !== undefined) {
          const currentDay = days[currentSection.dayIndex];
          if (currentDay) {
            const dayStops = validStops.filter(stop => stop.dayIds?.includes(currentDay.id));
            const currentStop = dayStops[currentSection.stopIndex];
            return currentStop ? [currentStop.id] : [];
          }
        }
        return [];
      
      default:
        return [];
    }
  };

  const activeStopIds = getActiveStops();

  // Create GeoJSON for day routes (lines connecting stops within each day)
  const dayRoutes = useMemo(() => {
    if (!days || !validStops) return [];
    
    const routes = days.map(day => {
      // Get stops for this day
      const dayStops = validStops.filter(stop => stop.dayIds?.includes(day.id));
      
      // Sort stops by their order in the day using stopIds array
      const sortedStops = dayStops.sort((a, b) => {
        const aIndex = day.stopIds?.indexOf(a.id) ?? -1;
        const bIndex = day.stopIds?.indexOf(b.id) ?? -1;
        return aIndex - bIndex;
      });
      
      console.log(`Day ${day.number}: ${dayStops.length} stops, ${sortedStops.length} sorted stops`);
      
      if (sortedStops.length < 2) return null; // Need at least 2 stops to draw a line
      
      // Create LineString coordinates
      const coordinates = sortedStops.map(stop => [
        stop.place.coordinates!.longitude,
        stop.place.coordinates!.latitude
      ]);
      
      console.log(`Day ${day.number} coordinates:`, coordinates);
      
      // Structure GeoJSON as a single Feature (like in the old implementation)
      const geoJson = {
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates
        }
      };
      
      const color = DAY_COLOR_HEX[day.number - 1] || '#007cbf';
      console.log(`Day ${day.number} color:`, color);
      
      return {
        dayId: day.id,
        dayNumber: day.number,
        geoJson,
        color
      };
    }).filter(Boolean) as Array<{
      dayId: string;
      dayNumber: number;
      geoJson: any;
      color: string;
    }>;
    
    console.log('Generated day routes:', routes);
    return routes;
  }, [days, validStops]);

  // Calculate bounds to fit all markers
  const bounds = useMemo(() => {
    if (validStops.length === 0) return null;
    
    const lngs = validStops.map(stop => stop.place.coordinates!.longitude);
    const lats = validStops.map(stop => stop.place.coordinates!.latitude);
    
    return {
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats)
    };
  }, [validStops]);

  // Calculate initial view state
  const initialViewState = useMemo(() => {
    if (validStops.length === 0) {
      return {
        longitude: 0,
        latitude: 20,
        zoom: 1
      };
    }
    
    if (validStops.length === 1) {
      return {
        longitude: validStops[0].place.coordinates?.longitude,
        latitude: validStops[0].place.coordinates?.latitude,
        zoom: 14
      };
    }
    
    if (bounds) {
      const centerLng = (bounds.minLng + bounds.maxLng) / 2;
      const centerLat = (bounds.minLat + bounds.maxLat) / 2;
      
      // Calculate zoom based on bounds size
      const lngDiff = bounds.maxLng - bounds.minLng;
      const latDiff = bounds.maxLat - bounds.minLat;
      const maxDiff = Math.max(lngDiff, latDiff);
      
      let zoom = 10;
      if (maxDiff < 0.01) zoom = 16;
      else if (maxDiff < 0.1) zoom = 13;
      else if (maxDiff < 1) zoom = 10;
      else if (maxDiff < 5) zoom = 8;
      else zoom = 5;
      
      return {
        longitude: centerLng,
        latitude: centerLat,
        zoom
      };
    }
    
    return {
      longitude: 0,
      latitude: 20,
      zoom: 1
    };
  }, [validStops, bounds]);

  // Fit bounds when section changes (but not when visibleStop or visibleDay changes, and not during scrolling)
  useEffect(() => {
    if (mapRef.current && bounds && validStops.length > 1 && !visibleStop && !visibleDay && !isScrolling) {
      const map = mapRef.current.getMap();
      
      setTimeout(() => {
        map.fitBounds(
          [
            [bounds.minLng, bounds.minLat],
            [bounds.maxLng, bounds.maxLat]
          ],
          {
            padding: { top: 40, bottom: 100, left: 40, right: 40 },
            duration: 2000,
            maxZoom: 16
          }
        );
      }, 100);
    }
  }, [bounds, validStops.length, visibleStop, visibleDay, isScrolling]);

  // Zoom to fit all stops when overview is visible (but not during scrolling)
  useEffect(() => {
    if (isOverviewVisible && mapRef.current && bounds && validStops.length > 0 && !isScrolling) {
      const map = mapRef.current.getMap();
      
      if (validStops.length === 1) {
        // Single stop - just fly to it
        const stop = validStops[0];
        map.flyTo({
          center: [stop.place.coordinates.longitude, stop.place.coordinates.latitude],
          zoom: 14,
          duration: 1000,
          essential: true
        });
      } else {
        // Multiple stops - fit bounds to show all
        setTimeout(() => {
          map.fitBounds(
            [
              [bounds.minLng, bounds.minLat],
              [bounds.maxLng, bounds.maxLat]
            ],
            {
              padding: { top: 40, bottom: 100, left: 40, right: 40 },
              duration: 1000,
              maxZoom: 16
            }
          );
        }, 100);
      }
    }
  }, [isOverviewVisible, bounds, validStops, isScrolling]);

  return (
    <div className="relative h-full overflow-hidden">
      {/* Mapbox Container */}
      <div className="absolute inset-0">
        <Map
          ref={mapRef}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          initialViewState={initialViewState}
        >
          {/* Render day routes (lines connecting stops within each day) */}
          {dayRoutes.map(dayRoute => (
            <Source 
              key={`day-route-${dayRoute.dayId}`}
              id={`day-route-${dayRoute.dayId}`} 
              type="geojson" 
              data={dayRoute.geoJson}
            >
              <Layer
                id={`day-route-line-${dayRoute.dayId}`}
                type="line"
                source={`day-route-${dayRoute.dayId}`}
                layout={{
                  'line-join': 'round',
                  'line-cap': 'round'
                }}
                paint={{
                  'line-color': dayRoute.color,
                  'line-width': 4,
                  'line-opacity': 0.8
                }}
              />
            </Source>
          ))}

          {/* Render stop markers */}
          {validStops.map((stop, index) => {
            // Find which day this stop belongs to
            const currentDay = days?.find(day => day.stopIds?.includes(stop.id));
            const dayColor = currentDay ? DAY_COLOR_HEX[currentDay.number - 1] : '';
            
            // Determine if this stop is active
            const isActive = activeStopIds.includes(stop.id);
            const isDimmed = activeStopIds.length > 0 && !isActive;
            const isVisibleStop = visibleStop?.id === stop.id;
            
            return (
              <Marker
                key={stop.id}
                longitude={stop.place.coordinates?.longitude}
                latitude={stop.place.coordinates?.latitude}
                anchor="center"
              >
                <button
                  className={`transition-all duration-300 transform ${
                    isVisibleStop
                      ? 'scale-125 z-10' 
                      : isActive 
                        ? 'scale-125' 
                        : isDimmed 
                          ? 'scale-90' 
                          : 'scale-100'
                  }`}
                  onClick={() => {
                    const stopIndexInDay = validStops
                      .filter(s => stop.dayIds?.[0] && s.dayIds?.includes(stop.dayIds[0]))
                      .findIndex(s => s.id === stop.id);
                    const dayIndex = currentDay ? currentDay.number - 1 : -1;
                    onStopClick(stop, dayIndex, stopIndexInDay);
                  }}
                >
                  <div
                    className={`w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center ${
                      isVisibleStop 
                        ? 'ring-4 ring-white ring-opacity-80 shadow-2xl' 
                        : isActive 
                          ? 'ring-2 ring-white ring-opacity-60' 
                          : ''
                    }`}
                    style={{ backgroundColor: dayColor }}
                  >
                    <span className="text-xs font-bold text-white">{stop.index}</span>
                  </div>
                  
                  {/* Stop label */}
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    {stop.title || `Stop ${index + 1}`}
                  </div>
                </button>
              </Marker>
            );
          })}

          {/* Geolocation Control */}
          <GeolocateControl
            position="top-right"
            positionOptions={{ enableHighAccuracy: true }}
            trackUserLocation={true}
            showAccuracyCircle={false}
          />
        </Map>
      </div>

      {/* Map Header */}
        <Button
        onClick={() => router.history.back()}
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 z-40 h-6 w-6 text-white hover:bg-white/20 rounded-full backdrop-blur-md bg-black/30"
        >
            <ArrowLeft className="h-4 w-4" />
        </Button>

      {/* Map overlay info */}
      <div className="absolute bottom-2 left-4 bg-white z-50 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 shadow-lg">
        <div className="text-[10px] sm:text-sm font-medium text-gray-900 leading-tight">
          {visibleStop ? `Viewing: ${visibleStop.title}` : 
           visibleDay ? `Day ${(days?.findIndex(d => d.id === visibleDay.id) ?? -1) + 1}: ${visibleDay.stopIds?.length} stops` :
           currentSection.type === "overview" ? `All Stops (${stops?.length || 0})` :
           currentSection.type === "day" && currentSection.dayIndex !== undefined ? 
             `Day ${currentSection.dayIndex + 1} (${activeStopIds.length} stops)` :
           currentSection.type === "stop" ? "Current Stop" : ""}
        </div>
        <div className="text-[8px] sm:text-xs text-gray-600 leading-tight">
          {visibleStop ? "Scroll to see other stops" :
           visibleDay ? "Viewing day overview - scroll for individual stops" :
           currentSection.type === "overview" ? `${days?.length || 0} days total` :
           currentSection.type === "day" ? "Click pins to jump to stops" :
           currentSection.type === "stop" ? "Viewing individual stop" : ""}
        </div>
      </div>
    </div>
  )
} 